<script setup lang="ts">
import { ContentBlock } from '~/components/ui/content-block'
import { LucideCopy, LucideNewspaper } from 'lucide-vue-next'
import type { IBankDetailsItem } from '~/components/widget/bank-page/details'
import { useToast } from '~/components/ui/toast'

const config = useRuntimeConfig()
const { toast } = useToast()
const props = defineProps<{ details: IBankDetailsItem[]; slug: string }>()

const { t } = useI18n()

const copied = ref<boolean>(false)

const formattedDetails = computed(() =>
  props.details.map((item) => `${t(item.name)}: ${item.description}`).join('\n')
)

const copyDetails = async () => {
  try {
    await navigator.clipboard.writeText(formattedDetails.value)
    copied.value = true
    setTimeout(() => (copied.value = false), 2000)
    toast({
      title: t('widget.bank_page.details.copied_success'),
      variant: 'success'
    })
  } catch (err) {
    console.error('Copy error:', err)
    toast({
      title: t('widget.bank_page.details.copied_error'),
      variant: 'destructive'
    })
  }
}
</script>

<template>
  <ContentBlock
    :heading-text="$t('widget.bank_page.details.heading')"
    variant="white"
    heading-tag="h2"
    heading-class="h4"
  >
    <table class="w-full">
      <tbody>
        <tr v-for="(item, index) in props.details" :key="index" class="border-b">
          <td class="py-4 text-gray-500">{{ $t(item.name) }}</td>
          <td class="py-4">{{ item.description }}</td>
        </tr>
      </tbody>
    </table>
    <div class="flex flex-col gap-5 md:flex-row">
      <Button as-child variant="outline-blue">
        <NuxtLinkLocale :to="`${config.public.baseApiUrl}/banks/${slug}/download-pdf`">
          <LucideNewspaper />{{ $t('widget.bank_page.details.download_pdf') }}
        </NuxtLinkLocale>
      </Button>
      <Button variant="outline-blue" @click="copyDetails">
        <LucideCopy />{{ $t('widget.bank_page.details.copy_details') }}
      </Button>
    </div>
  </ContentBlock>
</template>
